/**
 * useBookingDetailData — Fetch reservation detail for BookingDetailModal
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 5b-3: Extracted from BookingDetailModal.jsx detail fetch/effect/state.
 *
 * Purpose:
 *   Fetch reservation detail when modal opens and reservation_id is set. Same endpoint, params,
 *   gating, and loading/error behavior as the original inline effect. Clears when modal closes.
 *
 * Workflow:
 *   1. When isOpen && reservationId: setDetailLoading(true), setDetailError(null), call
 *      getReservationDetailFn(reservationId), map response to detailResponse (outcome, reservation,
 *      finance, financials_persisted_summary, etc.), setDetailData(detailResponse) or setDetailError.
 *   2. When !isOpen: setDetailData(null), setDetailError(null).
 *   3. AbortController + isActive to block setState after unmount/abort; AbortError silently ignored.
 *
 * Assumptions & Invariants:
 *   - getReservationDetailFn(reservationId) returns a Promise resolving to API shape
 *     { outcome, reservation, finance, finance_fields_meta, sources, canonical_financial_summary,
 *       financials_persisted_summary, persisted_available, persisted_unavailable_reason }.
 *   - Effect deps [isOpen, reservationId] preserve original trigger (modal used [isOpen, booking]).
 *   - No new endpoints; no change to call count or timing.
 *
 * Dependencies:
 *   - React (useState, useEffect)
 *   - getReservationDetailFn passed in by caller (e.g. ownerApi.getReservationDetail)
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 5b-3: Extracted from BookingDetailModal.jsx; AbortController + isActive added.
 */

import { useState, useEffect, useCallback } from 'react';

function buildDetailResponse(data) {
  // Support top-level or data.data envelope (staging/prod may differ)
  const payload = data.data != null ? { ...data, ...data.data } : data;
  return {
    reservation: payload.reservation || {},
    finance: payload.finance || {},
    finance_fields_meta: payload.finance_fields_meta || {},
    sources: payload.sources || {},
    canonical_financial_summary: payload.canonical_financial_summary || null,
    financials_persisted_summary: payload.financials_persisted_summary || null,
    persisted_available: payload.persisted_available ?? false,
    persisted_unavailable_reason: payload.persisted_unavailable_reason || null,
  };
}

/**
 * @param {Object} opts
 * @param {boolean} opts.isOpen - Modal open
 * @param {string|number|null|undefined} opts.reservationId - Reservation ID (e.g. booking?.reservation_id || booking?.id)
 * @param {function(string|number): Promise<Object>} opts.getReservationDetailFn - e.g. getReservationDetail from ownerApi
 * @returns {{ detailLoading: boolean, detailError: string|null, detailData: Object|null, refreshDetailData: function }}
 */
export function useBookingDetailData({ isOpen, reservationId, getReservationDetailFn }) {
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const refreshDetailData = useCallback(() => {
    if (!reservationId) return;
    setDetailLoading(true);
    setDetailError(null);
    getReservationDetailFn(reservationId)
      .then((data) => {
        if (data.outcome === 'success') {
          setDetailData(buildDetailResponse(data));
        } else {
          setDetailError(data.message || 'Failed to load reservation detail');
        }
      })
      .catch((err) => {
        console.error('[useBookingDetailData] Refresh fetch error:', err);
        setDetailError(err.message || 'Failed to load reservation detail');
      })
      .finally(() => {
        setDetailLoading(false);
      });
  }, [reservationId, getReservationDetailFn]);

  useEffect(() => {
    const abortController = new AbortController();
    let isActive = true;

    if (isOpen && reservationId) {
      const perfDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1';
      if (perfDebug) {
        console.log('[useBookingDetailData] Detail fetch initiated:', {
          reservation_id: reservationId,
          endpoint: `/api/v1/owner/reservations/${reservationId}`,
        });
      }

      if (isActive) {
        setDetailLoading(true);
        setDetailError(null);
      }

      const rid = reservationId;
      getReservationDetailFn(rid)
        .then((data) => {
          if (!isActive) return;
          if (data.outcome === 'success') {
            const detailResponse = buildDetailResponse(data);
            if (perfDebug) {
              console.log('[useBookingDetailData] Detail fetch success:', {
                finance_keys: Object.keys(detailResponse.finance || {}),
                finance_count: Object.keys(detailResponse.finance || {}).length,
                endpoint_used: 'detail',
              });
            }
            setDetailData(detailResponse);
          } else {
            throw new Error(data.message || 'Failed to load reservation detail');
          }
        })
        .catch((err) => {
          if (!isActive) return;
          if (err?.name === 'AbortError') return;
          console.error('[useBookingDetailData] Error fetching reservation detail:', err);
          setDetailError(err.message || 'Failed to load reservation detail');
          if (perfDebug) {
            console.error('[useBookingDetailData] Detail fetch error:', { error: err.message, reservation_id: rid });
          }
        })
        .finally(() => {
          if (isActive) setDetailLoading(false);
        });

      return () => {
        abortController.abort();
        isActive = false;
      };
    }

    if (!isOpen) {
      setDetailData(null);
      setDetailError(null);
    }

    return () => {
      abortController.abort();
      isActive = false;
    };
  }, [isOpen, reservationId]);

  return { detailLoading, detailError, detailData, refreshDetailData };
}
